import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  content: {
    minHeight: 120,
    maxHeight: "55vh",
    paddingTop: theme.spacing(1),
  },
  snippet: {
    wordBreak: "break-word",
    "& mark": {
      backgroundColor: theme.palette.type === "dark" ? "#5c4a00" : "#fff59d",
      padding: "0 2px",
    },
  },
  meta: {
    marginTop: 4,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
}));

const DEBOUNCE_MS = 480;

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightSnippet(text, term) {
  if (!text || !term) return text;
  try {
    const re = new RegExp(`(${escapeRegExp(term)})`, "gi");
    return String(text).replace(re, "<mark>$1</mark>");
  } catch (_) {
    return text;
  }
}

function resolveMessageContactName(msg) {
  const fromMessage = msg?.contact?.name;
  const fromTicket = msg?.ticket?.contact?.name;
  const name = (fromMessage && String(fromMessage).trim()) || (fromTicket && String(fromTicket).trim());
  return name || "";
}

/**
 * Com busca em mensagens ativa e ticket aberto: debounce do termo, lista ocorrências no atendimento
 * e ao clicar rola até a mensagem no chat (evento já tratado em MessagesList).
 */
const TicketMessagesSearchModal = ({
  ticketUuid,
  searchOnMessages,
  rawSearchInput,
  /** Muda a cada clique na lista (ex.: uuid v4 em TicketListItemCustom) — permite reabrir o modal com o mesmo termo. */
  ticketSelectionKey,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [activeTerm, setActiveTerm] = useState("");
  const debounceRef = useRef(null);
  /** Última busca concluída com sucesso (para gravar o que o usuário “dispensou” ao fechar). */
  const lastSearchRef = useRef({ term: "", ticketUuid: null, selectionKey: null });
  /** Se o último fechamento foi para esse snapshot, não reabrimos até uuid/termo/seleção mudarem. */
  const dismissedSnapshotRef = useRef(null);
  const selectionKeyRef = useRef(ticketSelectionKey);
  selectionKeyRef.current = ticketSelectionKey;

  useEffect(() => {
    dismissedSnapshotRef.current = null;
  }, [ticketUuid]);

  const runSearch = useCallback(async (term) => {
    if (!ticketUuid || !term || term.length < 2) {
      setResults([]);
      setCount(0);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/messages/${ticketUuid}/search`, {
        params: { searchParam: term, pageNumber: 1 },
      });
      const list = data.messages || [];
      setResults(list);
      setCount(typeof data.count === "number" ? data.count : list.length);
      const sel = selectionKeyRef.current;
      lastSearchRef.current = { term, ticketUuid, selectionKey: sel };
      const snap = dismissedSnapshotRef.current;
      const sameDismissed =
        snap &&
        snap.term === term &&
        snap.ticketUuid === ticketUuid &&
        snap.selectionKey === sel;
      if (!sameDismissed) {
        setOpen(true);
      }
    } catch (err) {
      toastError(err);
      setResults([]);
      setCount(0);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [ticketUuid]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchOnMessages || !ticketUuid) {
      setOpen(false);
      setResults([]);
      setActiveTerm("");
      dismissedSnapshotRef.current = null;
      return undefined;
    }

    const t = (rawSearchInput || "").trim();
    if (t.length < 2) {
      setOpen(false);
      setResults([]);
      setActiveTerm("");
      dismissedSnapshotRef.current = null;
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      setActiveTerm(t);
      runSearch(t);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawSearchInput, searchOnMessages, ticketUuid, ticketSelectionKey, runSearch]);

  const handleClose = () => {
    dismissedSnapshotRef.current = { ...lastSearchRef.current };
    setOpen(false);
  };

  const handlePickMessage = (message) => {
    const tid = message.ticketId ?? message.ticket?.id;
    if (message.id == null || tid == null) return;
    window.dispatchEvent(
      new CustomEvent("mf-scroll-ticket-message", {
        detail: { messageId: message.id, ticketId: tid },
      })
    );
    handleClose();
  };

  if (!searchOnMessages) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {i18n.t("ticketsList.ticketMessageSearchModal.title")}
        {activeTerm ? (
          <Typography variant="caption" display="block" color="textSecondary">
            “{activeTerm}” · {count}{" "}
            {i18n.t("ticketsList.ticketMessageSearchModal.resultsSuffix")}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : results.length === 0 && activeTerm.length >= 2 ? (
          <Typography className={classes.empty} variant="body2">
            {i18n.t("ticketsList.ticketMessageSearchModal.empty")}
          </Typography>
        ) : (
          <List dense disablePadding>
            {results.map((msg) => {
              const contactName = resolveMessageContactName(msg);
              return (
              <ListItem
                key={msg.id}
                button
                onClick={() => handlePickMessage(msg)}
                alignItems="flex-start"
              >
                <ListItemText
                  primary={
                    <>
                      <Typography component="div" variant="caption" className={classes.contactName}>
                        {contactName ||
                          i18n.t("ticketsList.ticketMessageSearchModal.contactUnknown")}
                      </Typography>
                      <span
                        className={classes.snippet}
                        dangerouslySetInnerHTML={{
                          __html: highlightSnippet(
                            (msg.body || "").slice(0, 220) +
                              ((msg.body || "").length > 220 ? "…" : ""),
                            activeTerm
                          ),
                        }}
                      />
                    </>
                  }
                  secondary={
                    <span className={classes.meta}>
                      {msg.createdAt
                        ? new Date(msg.createdAt).toLocaleString(
                            typeof i18n.language === "string" && i18n.language.startsWith("en")
                              ? "en-US"
                              : "pt-BR"
                          )
                        : ""}
                      {msg.fromMe
                        ? ` · ${i18n.t("ticketsList.ticketMessageSearchModal.you")}`
                        : ""}
                    </span>
                  }
                />
              </ListItem>
            );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {i18n.t("ticketsList.ticketMessageSearchModal.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketMessagesSearchModal;
